import { useEffect, useRef } from 'react';
import { useSwipeable } from 'react-swipeable';
import cn from 'classnames';
import SVGArrow from '@svg/arrow.svg';
import styles from './carousel.module.css';
import { useSlider } from '../../../hooks/use-slider';
import { keyCodes } from '../../../constants/keyboard/keyboard-constans';

function Carousel({
  children,
  onSlideChange,
  currentSlideIndex = 0,
  slidesLength = 0,
  cyclic,
  autoPlayInterval,
  autoPlay = false,
  id,
  showArrows = false,
  hidePositionIndicators = false
}) {
  // Tells if the slider container has actually the focus.
  const hasFocus = useRef(false);

  // A ref to the element acting as wrapper of the carousel.
  const wrapperRef = useRef();

  /**
   * The API from the slider hook.
   */
  const { moveToNextSlide, moveToPrevSlide, setAutoplayTimeout, clearAutoplayTimeout } = useSlider(
    onSlideChange,
    currentSlideIndex,
    slidesLength,
    cyclic,
    autoPlay,
    autoPlayInterval
  );

  // The handlers of swipe.
  const swipeHandlers = useSwipeable({
    onSwipedLeft: () => {
      moveToNextSlide();
    },
    onSwipedRight: () => {
      moveToPrevSlide();
    }
  });

  /**
   * Clears the timeout associated with the slider autoplay.
   */
  const resetAutoplay = () => {
    clearAutoplayTimeout();
  };

  /**
   * Sets the autoplay timeout.
   */
  const setAutoplay = () => {
    if (!hasFocus.current) {
      setAutoplayTimeout();
    }
  };

  /**
   * Handles arrow key press.
   * @param {*} event
   */
  const handleArrowKeyPress = (event) => {
    if (event.keyCode === keyCodes.LEFT_ARROW) {
      moveToPrevSlide();
    } else if (event.keyCode === keyCodes.RIGHT_ARROW) {
      moveToNextSlide();
    } else {
      (() => {})();
    }
  };

  /**
   * The mouse enter handler.
   */
  const onMouseEnter = () => {
    resetAutoplay();
  };

  /**
   * The mouse leave handler.
   */
  const onMouseLeave = () => {
    if (hasFocus.current) {
      return;
    }
    setAutoplay();
  };

  /**
   * The handler for the on-focus event on the slider container.
   */
  const onContainerFocus = () => {
    hasFocus.current = true;
    resetAutoplay();
  };

  /**
   * The handler for the on-focus event on the slider container.
   */
  const onContainerBlur = () => {
    hasFocus.current = false;
    setAutoplay();
  };

  /**
   * Setup work when the component is mounted.
   */
  useEffect(() => {
    if (!hasFocus.current) {
      setAutoplay();
    }

    // Cleanup.
    return () => {
      resetAutoplay();
    };
  }, [currentSlideIndex]);

  const style = `${currentSlideIndex * 100}%`;

  return (
    <div
      ref={wrapperRef}
      onBlur={onContainerBlur}
      onFocus={onContainerFocus}
      onMouseLeave={onMouseLeave}
      onMouseEnter={onMouseEnter}
      onKeyDown={handleArrowKeyPress}
      role="button"
      tabIndex="0"
      className={styles.divWrapper}
      {...swipeHandlers}
    >
      <div id={id} className="carousel slide" data-ride="carousel">
        <div className="carousel-inner">
          <div
            className={cn(styles.divCarouselInner, styles.carouselTrack)}
            style={{ transform: `translateX(-${style})` }}
          >
            {children}
          </div>
        </div>
        {showArrows ? (
          <>
            <button
              className={`${styles.wrapperButton} ${styles.arrows}`}
              onClick={moveToPrevSlide}
              type="button"
            >
              <SVGArrow className={`${styles.arrowLeft}`} />
            </button>
            <button
              className={`${styles.wrapperButton} ${styles.arrows} ${styles.arrowRight}`}
              onClick={moveToNextSlide}
              type="button"
            >
              <SVGArrow />
            </button>
          </>
        ) : null}
      </div>
      {!hidePositionIndicators ? (
        <ol className={`${styles.dotsContainer} carousel-indicators`}>
          {Array(slidesLength)
            .fill()
            ?.map((slide, index) => (
              <button
                type="button"
                key={`slide_item_${index + 1}`}
                className={styles.wrapperButton}
                onClick={() => onSlideChange(index)}
              >
                <li
                  className={`${styles.active} ${currentSlideIndex === index ? 'active' : ''}`}
                  aria-label={`Slide ${index + 1}`}
                />
              </button>
            ))}
        </ol>
      ) : null}
    </div>
  );
}

Carousel.displayName = 'Carousel';
export { Carousel };
